import React from 'react';
import { UI_PREFERENCE_KEYS, uiPreferencesService } from '../services/uiPreferencesService';
import {
  DEFAULT_SIDEBAR_LAYOUT,
  isSidebarLayoutCustomized,
  normalizeSidebarLayout,
  readSidebarLayout,
  SidebarLayout,
  writeSidebarLayout,
} from './sidebarLayout';

const SAVE_DEBOUNCE_MS = 600;

type UseSidebarLayoutOptions = {
  tenantId: string | null;
  employeeId: string | null;
  username: string | null;
};

type UseSidebarLayoutResult = {
  layout: SidebarLayout;
  updateLayout: (updater: (current: SidebarLayout) => SidebarLayout) => void;
};

/**
 * Personal sidebar layout with a local cache and server sync.
 *
 * The cached copy in localStorage renders immediately; the server copy (one row per employee in
 * `ui-preferences`) replaces it once loaded so the layout follows the person across devices.
 * Writes go to the cache at once and to the server after a short debounce; a failed save keeps
 * the local copy, so the sidebar never snaps back under the person's hands.
 */
export const useSidebarLayout = ({ tenantId, employeeId, username }: UseSidebarLayoutOptions): UseSidebarLayoutResult => {
  const scopeKey = `${tenantId ?? ''}|${employeeId ?? ''}|${username ?? ''}`;
  const [layout, setLayout] = React.useState<SidebarLayout>(() => readSidebarLayout(tenantId, employeeId, username));
  const loadedScope = React.useRef(scopeKey);
  const saveTimer = React.useRef<number | null>(null);
  const pendingSave = React.useRef<SidebarLayout | null>(null);
  const canSync = Boolean(tenantId && employeeId);

  const flushSave = React.useCallback(() => {
    if (saveTimer.current !== null) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }

    const next = pendingSave.current;
    pendingSave.current = null;
    if (!next) {
      return;
    }

    void uiPreferencesService
      .updateMine({ [UI_PREFERENCE_KEYS.sidebarLayout]: isSidebarLayoutCustomized(next) ? next : null })
      .catch(() => {
        // The local copy already applies; the next change retries the save.
      });
  }, []);

  React.useEffect(() => {
    if (loadedScope.current !== scopeKey) {
      loadedScope.current = scopeKey;
      pendingSave.current = null;
      if (saveTimer.current !== null) {
        window.clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      setLayout(readSidebarLayout(tenantId, employeeId, username));
    }

    if (!canSync) {
      return;
    }

    let cancelled = false;
    void uiPreferencesService.getMine()
      .then(result => {
        if (cancelled || pendingSave.current) {
          return;
        }

        const remote = result.preferences[UI_PREFERENCE_KEYS.sidebarLayout];
        const next = remote === undefined || remote === null ? DEFAULT_SIDEBAR_LAYOUT : normalizeSidebarLayout(remote);
        writeSidebarLayout(next, tenantId, employeeId, username);
        setLayout(next);
      })
      .catch(() => {
        // Offline or older server: the cached copy stays in charge.
      });

    return () => {
      cancelled = true;
    };
  }, [canSync, employeeId, scopeKey, tenantId, username]);

  React.useEffect(() => () => {
    if (saveTimer.current !== null) {
      window.clearTimeout(saveTimer.current);
    }
  }, []);

  const updateLayout = React.useCallback((updater: (current: SidebarLayout) => SidebarLayout) => {
    setLayout(current => {
      const next = updater(current);
      if (next === current) {
        return current;
      }

      writeSidebarLayout(next, tenantId, employeeId, username);
      if (canSync) {
        pendingSave.current = next;
        if (saveTimer.current !== null) {
          window.clearTimeout(saveTimer.current);
        }
        saveTimer.current = window.setTimeout(flushSave, SAVE_DEBOUNCE_MS);
      }
      return next;
    });
  }, [canSync, employeeId, flushSave, tenantId, username]);

  return { layout, updateLayout };
};
